import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface BlackWhiteLogoUploadProps {
  currentLogoPath?: string;
  onLogoChange: (logoPath: string | null) => void;
}

const BlackWhiteLogoUpload: React.FC<BlackWhiteLogoUploadProps> = ({
  currentLogoPath,
  onLogoChange,
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validiere Dateityp
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wählen Sie eine Bilddatei aus');
      return;
    }

    // Validiere Dateigröße (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Die Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    // Erstelle Vorschau
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!fileInputRef.current?.files?.[0]) {
      toast.error('Bitte wählen Sie eine Datei aus');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', fileInputRef.current.files[0]);

      const response = await apiService.post<{ logoPath: string }>('/system/logo-bw/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      onLogoChange(response.logoPath);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast.success('Schwarz-Weiß-Logo erfolgreich hochgeladen');
    } catch (error) {
      console.error('Error uploading black & white logo:', error);
      toast.error('Fehler beim Hochladen des Schwarz-Weiß-Logos');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setUploading(true);
    try {
      await apiService.delete('/system/logo-bw');
      onLogoChange(null);
      setPreviewUrl(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      toast.success('Schwarz-Weiß-Logo erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting black & white logo:', error);
      toast.error('Fehler beim Löschen des Schwarz-Weiß-Logos');
    } finally {
      setUploading(false);
    }
  };

  const getLogoUrl = (path: string) => {
    return path; // Das Logo ist bereits ein Base64-String
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Schwarz-Weiß-Logo für Labels
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Aktuelles Logo */}
        {(currentLogoPath || previewUrl) && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Aktuelles Schwarz-Weiß-Logo:</h4>
            <div className="relative inline-block">
              <img
                src={previewUrl || getLogoUrl(currentLogoPath!)}
                alt="Schwarz-Weiß-Logo"
                className="h-16 w-auto max-w-48 object-contain border rounded"
              />
              {currentLogoPath && !previewUrl && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={uploading}
                  className="absolute -top-2 -right-2 h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Upload-Bereich */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm">
            {currentLogoPath ? 'Schwarz-Weiß-Logo ändern:' : 'Schwarz-Weiß-Logo hochladen:'}
          </h4>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4">
            <div className="text-center space-y-2">
              <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Klicken Sie hier oder ziehen Sie eine Bilddatei hierher
              </p>
              <p className="text-xs text-muted-foreground">
                Unterstützte Formate: JPG, PNG, GIF (max. 5MB)
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Hinweis:</strong> Dieses Logo wird für Labels verwendet und sollte schwarz-weiß sein
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                Datei auswählen
              </Button>
            </div>
          </div>
        </div>

        {/* Upload-Button */}
        {previewUrl && (
          <div className="flex gap-2">
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1"
            >
              {uploading ? 'Wird hochgeladen...' : 'Schwarz-Weiß-Logo hochladen'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setPreviewUrl(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              disabled={uploading}
            >
              Abbrechen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BlackWhiteLogoUpload;
